import React, { useState } from "react";
import {
  IResourceComponentsProps,
  BaseRecord,
  useTranslate,
  useCustomMutation,
  useInvalidate,
  useGetIdentity,
  useApiUrl,
} from "@refinedev/core";
import {
  useTable,
  List,
  ShowButton,
  DateField,
  DeleteButton,
} from "@refinedev/antd";
import { Table, Space, Button, Tag, message } from "antd";
import { GlassCard } from "../../shared/components";
import {
  DownloadOutlined,
  FileDoneOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useAdmin } from "../../hooks/auth";
import { CustomerBackButton } from "../orders/components/customerBackButton";
import { CustomerBreadcrumb } from "../orders/components/customerBreadcrumb";
import { UserRole } from "../../models/role";
import { theme } from "antd";
import { axiosInstance } from "../../authProvider";

export const TransactionList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();
  const invalidate = useInvalidate();
  const isAdmin = useAdmin();
  const { data: user } = useGetIdentity();
  const { token } = theme.useToken();
  const { tableProps } = useTable({
    syncWithLocation: true,
    resource: "transactions",
  });

  const { mutate: generateInvoice, isLoading: isGeneratingInvoice } =
    useCustomMutation();
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    number | null
  >(null);
  interface InvoiceDownloadData {
    qontoInvoiceUrl?: string | null;
  }
  const downloadLocalInvoicePdf = async (invoiceId: number): Promise<void> => {
    const response = await axiosInstance.get(`${apiUrl}/invoices/${invoiceId}/pdf`, {
      responseType: "blob",
    });
    const blob = new Blob([response.data], { type: "application/pdf" });
    const objectUrl = window.URL.createObjectURL(blob);
    const linkElement = document.createElement("a");
    linkElement.href = objectUrl;
    linkElement.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadInvoice = async (invoiceId: number) => {
    setDownloadingInvoiceId(invoiceId);
    try {
      const invoiceResponse = await axiosInstance.get<InvoiceDownloadData>(
        `${apiUrl}/invoices/${invoiceId}`,
      );
      const qontoInvoiceUrl = invoiceResponse.data?.qontoInvoiceUrl;
      if (qontoInvoiceUrl) {
        const openedWindow = window.open(
          qontoInvoiceUrl,
          "_blank",
          "noopener,noreferrer",
        );
        if (!openedWindow) {
          throw new Error("popup_blocked");
        }
        return;
      }
      await downloadLocalInvoicePdf(invoiceId);
    } catch (error) {
      message.error(translate("transactions.errors.downloadInvoiceFailed"));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleGenerateInvoice = (transactionId: number) => {
    generateInvoice(
      {
        url: `invoices/transaction/${transactionId}`,
        method: "post",
        values: {},
        errorNotification: false,
      },
      {
        onSuccess: () => {
          invalidate({ resource: "transactions", invalidates: ["list"] });
        },
        onError: (error: any) => {
          console.log("error", error);
          if (error?.response?.status === 409) {
            message.error(
              translate("transactions.errors.invoiceAlreadyExists"),
            );
          } else {
            message.error(
              translate("transactions.errors.generateInvoiceFailed"),
            );
          }
        },
      },
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <List
        canCreate={false}
        title={
          <>
            {user?.role?.id == UserRole.user && <CustomerBackButton />}
            {translate("transactions.titles.list")}
          </>
        }
        breadcrumb={
          <>
            {user?.role?.id == UserRole.user && (
              <CustomerBreadcrumb title={translate("actions.list")} />
            )}
          </>
        }
        resource="transactions"
      >
        <style>{`
          .transactions-list-table-view .ant-table { background: transparent !important; }
          .transactions-list-table-view .ant-table-thead > tr > th { background: transparent !important; }
          .transactions-list-table-view .ant-table-tbody > tr > td { background: transparent !important; }
        `}</style>
        <GlassCard className="transactions-list-table-view !p-0 overflow-hidden">
          <Table {...tableProps} rowKey="id">
            <Table.Column
              dataIndex={["createdAt"]}
              title={
                <>
                  <ClockCircleOutlined style={{ marginRight: 8 }} />{" "}
                  {translate("transactions.fields.createdAt")}
                </>
              }
              render={(value: any) => (
                <Tag
                  style={{
                    backgroundColor: token.colorBgElevated,
                    color: token.colorText,
                    borderColor: token.colorBorderBg,
                  }}
                >
                  <DateField
                    className="text-xs"
                    value={value}
                    format="DD MMM HH:mm"
                    style={{ color: token.colorPrimary }}
                  />
                </Tag>
              )}
              width={180}
            />
            <Table.Column
              dataIndex="amount"
              title={translate("transactions.fields.amount")}
              render={(value: any) => `${value} €`}
            />
            <Table.Column
              dataIndex="credit"
              title={translate("credits.credits")}
              render={(value: any) => `${value}`}
            />
            <Table.Column
              dataIndex="account"
              title={translate("transactions.fields.account")}
              render={(value: any) => value?.name}
            />
            <Table.Column
              title={translate("table.actions")}
              dataIndex="actions"
              render={(_, record: BaseRecord) => (
                <Space>
                  {record.invoice ? (
                    <>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloadingInvoiceId === record.invoice.id}
                        onClick={() => handleDownloadInvoice(record.invoice.id)}
                      >
                        {translate("transactions.titles.viewInvoice")}
                      </Button>
                      {isAdmin && (
                        <DeleteButton
                          resource="invoices"
                          recordItemId={record.invoice.id}
                          onSuccess={() =>
                            invalidate({
                              resource: "transactions",
                              invalidates: ["list"],
                            })
                          }
                        >
                          {translate("transactions.titles.deleteInvoice")}
                        </DeleteButton>
                      )}
                    </>
                  ) : (
                    <>
                      {isAdmin && (
                        <Button
                          icon={<FileDoneOutlined />}
                          loading={isGeneratingInvoice}
                          onClick={() =>
                            handleGenerateInvoice(record.id as number)
                          }
                        >
                          {translate("transactions.titles.generateInvoice")}
                        </Button>
                      )}
                    </>
                  )}
                  <ShowButton
                    hideText
                    hidden
                    size="small"
                    recordItemId={record.id}
                  />
                </Space>
              )}
            />
          </Table>
        </GlassCard>
      </List>
    </div>
  );
};
